import { Box, Divider, Typography, makeStyles, LinearProgress } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import { useRecoilState } from "recoil";

import { JobCategory } from "@/api/generated";
import { AnimationVideo } from "@/components/AnimationVideo";
import { CustomButton } from "@/components/Button";
import { CustomChip } from "@/components/Chip";
import { RegistrationPhases } from "@/components/registration/RegistrationPhases";
import { NumberEntry } from "@/components/selection/NumberEntry";
import { fontFamilies } from "@/designTokens";
import { RegistrationFlowLayout as RegistrationTemplate, RegistrationActions } from "@/layouts/RegistrationFlowLayout";
import theme from "@/theme";
import { AppEventTokenEnum, sendEventToAppMeasurementTool } from "@/utilities/capacitorLogic";
import {
  JOB_CATEGORY_OPTIONS,
  LOAN_RANGE_OPTIONS,
  MONTH_OPTIONS,
  OCCUPATION_OPTIONS,
  PERIOD_RANGE_OPTIONS,
  YEAR_OPTIONS,
} from "@/utilities/options";
import { annualIncomeSchema } from "@/validations/annualIncome";
import { UserRegistrationsAccoutFormState } from "@/view_models/atoms";

import PropertyImage from "@assets/image/investment_diagnosis_step_02_property.png";
import LoadingVideo from "@assets/image/investment_diagnosis_step_loading.mp4";
import LoadingPoster from "@assets/image/investment_diagnosis_step_loading_poster.jpg";

import AgeStep from "./components/steps/Age";
import AssetStep from "./components/steps/Asset";
import JobStep from "./components/steps/Job";

const useStyles = makeStyles(() => ({
  "@keyframes resultEnter": {
    from: { opacity: 0, transform: "translateY(12px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes resultCardEnter": {
    from: { opacity: 0, transform: "translateY(18px) scale(0.985)" },
    to: { opacity: 1, transform: "translateY(0) scale(1)" },
  },
  resultReveal: {
    animation: "$resultEnter 480ms cubic-bezier(0.22, 1, 0.36, 1) both",
    "@media (prefers-reduced-motion: reduce)": { "&&": { animation: "none" } },
  },
  resultCardReveal: {
    animationName: "$resultCardEnter",
    animationDuration: "600ms",
    animationDelay: "100ms",
  },
  answersReveal: { animationDelay: "200ms" },
  featuresReveal: { animationDelay: "280ms" },
  actionsReveal: { animationDelay: "360ms" },
  questionTitle: {
    fontSize: 16,
    fontWeight: 700,
    textAlign: "center",
    lineHeight: 1.3,
    whiteSpace: "pre-line",
    [theme.breakpoints.up("md")]: { fontSize: 20 },
  },
  questionDescription: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    textAlign: "center",
    marginTop: 8,
    [theme.breakpoints.up("md")]: { fontSize: 14 },
  },
  progressCaption: {
    textAlign: "center",
    fontSize: 12,
    fontFamily: fontFamilies.number,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginTop: 8,
  },
  loadingHeading: {
    fontSize: 20,
    fontWeight: 700,
    color: theme.palette.text.primary,
    textAlign: "center",
    marginBottom: theme.spacing(4),
    [theme.breakpoints.up("md")]: { fontSize: 24 },
  },
  loadingVideo: {
    width: 240,
    maxWidth: "80%",
    display: "block",
    margin: "0 auto",
  },
  loadingDoneText: {
    fontSize: 20,
    fontWeight: 700,
    color: theme.palette.primary.main,
    textAlign: "center",
    [theme.breakpoints.up("md")]: { fontSize: 24 },
  },
  resultTitle: {
    fontSize: 16,
    [theme.breakpoints.up("md")]: { fontSize: 20 },
    fontWeight: 700,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
    textAlign: "center",
    marginBottom: 8,
  },
  resultDescription: {
    fontSize: 12,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    textAlign: "center",
    marginBottom: theme.spacing(3),
    [theme.breakpoints.up("md")]: { fontSize: 14 },
  },
  answerChip: {
    "&&": {
      height: 28,
      fontFamily: fontFamilies.number,
      fontSize: 12,
      fontWeight: 400,
      border: `1px solid ${theme.palette.semantic["border/default"]}`,
      color: theme.palette.text.primary,
    },
  },
  buttonWrap: {
    width: "100%",
    maxWidth: 360,
    margin: "0 auto",
  },
  resultCard: {
    width: "100%",
    maxWidth: 600,
    margin: "0 auto",
    position: "relative",
  },
  blurText: {
    filter: "blur(6px)",
  },
  card: {
    borderRadius: 16,
    backgroundColor: theme.palette.semantic["surface/base"],
    boxShadow: "none",
    marginBottom: theme.spacing(3),
    overflow: "hidden",
    padding: theme.spacing(2.5),
  },
  // 見出しの「推定運用可能資産額」だけはぼかさず、金額から下をぼかしてバッジを重ねる
  blurredArea: {
    position: "relative",
    marginTop: theme.spacing(1.5),
  },
  blurredContent: {
    filter: "blur(8px)",
    pointerEvents: "none",
  },
  assetSection: {
    display: "flex",
    justifyContent: "center",
  },
  cardDivider: {
    margin: theme.spacing(2, 0),
  },
  potentialLabel: {
    fontWeight: 400,
    fontSize: 12,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
  potentialValueWrap: {
    display: "flex",
    alignItems: "baseline",
  },
  potentialValueNumber: {
    fontWeight: 700,
    fontSize: 20,
    lineHeight: 1.2,
    letterSpacing: 0.6,
    color: theme.palette.primary.main,
  },
  potentialValueUnit: {
    fontWeight: 700,
    fontSize: 14,
    lineHeight: 1.5,
    letterSpacing: 0.42,
    color: theme.palette.primary.main,
    marginLeft: theme.spacing(0.5),
  },
  potentialValueSuffix: {
    fontWeight: 700,
    fontSize: 14,
    lineHeight: 1.5,
    letterSpacing: 0.42,
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(0.5),
  },
  propertySection: {
    display: "flex",
    gap: theme.spacing(1.5),
  },
  propertyImage: {
    width: 122,
    height: 132,
    objectFit: "cover",
    flexShrink: 0,
  },
  propertyText: {
    flex: 1,
    minWidth: 0,
    gap: theme.spacing(0.5),
    display: "flex",
    flexDirection: "column",
  },
  propertyHeading: {
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
  },
  propertyTitle: {
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 1.2,
    letterSpacing: 0.42,
  },
  propertySpeedLabel: {
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 1.5,
    letterSpacing: 0.42,
  },
  propertySpeedValue: {
    display: "flex",
    alignItems: "baseline",
    gap: theme.spacing(0.5),
    fontWeight: 700,
    fontSize: 18,
    lineHeight: 1.2,
    letterSpacing: 0.54,
    color: theme.palette.primary.main,
  },
  propertySpeedValueMeta: {
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 1.5,
    letterSpacing: 0.42,
    color: theme.palette.primary.main,
  },
  wealthNote: {
    fontSize: 12,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  registerBadge: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    width: "fit-content",
    borderRadius: 4,
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    fontSize: 12,
    fontWeight: 400,
  },
  registerBadgeIcon: {
    display: "block",
    flexShrink: 0,
    width: 14,
    height: 14,
  },
  featuresLead: {
    fontSize: 14,
    fontWeight: 700,
    marginBottom: theme.spacing(1.5),
  },
  featuresBox: {
    backgroundColor: theme.palette.semantic["surface/base"],
    borderRadius: 16,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  featureItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    "&:not(:last-child)": {
      marginBottom: theme.spacing(1),
    },
  },
  featureText: {
    fontSize: 14,
    lineHeight: 1.6,
  },
  featureHighlight: {
    color: theme.palette.text.primary,
  },
}));

const STEP_AGE = 0;
const STEP_JOB = 1;
const STEP_INCOME = 2;
const STEP_ASSET = 3;
const STEP_LOAN = 4;
const STEP_LOADING = 5;
const STEP_RESULT = 6;

const STEP_EVENT_MAP: Record<number, AppEventTokenEnum> = {
  [STEP_AGE]: AppEventTokenEnum.PreInvestmentDiagnosisStep1,
  [STEP_JOB]: AppEventTokenEnum.PreInvestmentDiagnosisStep2,
  [STEP_ASSET]: AppEventTokenEnum.PreInvestmentDiagnosisStep3,
  [STEP_LOADING]: AppEventTokenEnum.PreInvestmentDiagnosisLoading,
  [STEP_RESULT]: AppEventTokenEnum.PreInvestmentDiagnosisComplete,
};

const YEAR_OPTIONS_ASC = [...YEAR_OPTIONS].reverse();
const JOB_INDUSTRY_REQUIRED_CATEGORIES = [
  JobCategory.会社員上場,
  JobCategory.会社員非上場,
  JobCategory.会社員外資系企業,
];

export const DiagnosisPrePage: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const [currentStep, setCurrentStep] = useState<number>(STEP_AGE);
  const [birthYear, setBirthYear] = useState<number | "">("");
  const [birthMonth, setBirthMonth] = useState<number | "">("");
  const [jobCategory, setJobCategory] = useState<string>("");
  const [jobIndustry, setJobIndustry] = useState<string>("");
  const [jobContinuousPeriod, setJobContinuousPeriod] = useState<string>("");
  const [annualIncome, setAnnualIncome] = useState<string>("");
  const [financialAssetRange, setFinancialAssetRange] = useState<string>("");
  const [holdingPropertyCount, setHoldingPropertyCount] = useState<string>("");
  const [realEstateLoanAmount, setRealEstateLoanAmount] = useState<string>("");
  const [videoEnded, setVideoEnded] = useState(false);
  const [accountFormData, setAccountFormData] = useRecoilState(UserRegistrationsAccoutFormState);
  // 経験なし（false）の場合のみ保有物件系の項目を非表示にする。
  // 未設定（undefined/null）の場合は既存挙動を壊さないよう「経験あり」と同様に表示する。
  const showPropertyOwnershipFields = accountFormData.has_investment_experience !== false;

  const handleLoadingFinish = useCallback(() => {
    setCurrentStep((prev) => (prev === STEP_LOADING ? STEP_RESULT : prev));
  }, []);

  const handleVideoEnd = useCallback(() => {
    setVideoEnded(true);
  }, []);

  useEffect(() => {
    if (currentStep !== STEP_LOADING) return;
    setVideoEnded(false);
    const timeoutId = window.setTimeout(() => setVideoEnded(true), 7000);
    return () => window.clearTimeout(timeoutId);
  }, [currentStep]);

  useEffect(() => {
    if (currentStep !== STEP_LOADING || !videoEnded) return;
    const timeoutId = window.setTimeout(handleLoadingFinish, 1500);
    return () => window.clearTimeout(timeoutId);
  }, [currentStep, videoEnded, handleLoadingFinish]);

  useEffect(() => {
    const eventToken = STEP_EVENT_MAP[currentStep];
    if (!eventToken) return;
    sendEventToAppMeasurementTool(eventToken);
  }, [currentStep]);

  const isAgeStepValid = birthYear !== "" && birthMonth !== "";
  const needsJobIndustry = useMemo(
    () => JOB_INDUSTRY_REQUIRED_CATEGORIES.includes(jobCategory as JobCategory),
    [jobCategory]
  );
  const isJobStepValid = jobCategory !== "" && jobContinuousPeriod !== "" && (!needsJobIndustry || jobIndustry !== "");
  const holdingPropertyCountNumber = useMemo(() => {
    if (!holdingPropertyCount) return 0;
    const parsed = Number(holdingPropertyCount);
    return Number.isNaN(parsed) ? 0 : parsed;
  }, [holdingPropertyCount]);
  const needsLoanAmount = showPropertyOwnershipFields && holdingPropertyCountNumber >= 1;
  useEffect(() => {
    if (!needsLoanAmount && realEstateLoanAmount) {
      setRealEstateLoanAmount("");
    }
  }, [needsLoanAmount, realEstateLoanAmount]);
  useEffect(() => {
    if (!needsJobIndustry && jobIndustry) {
      setJobIndustry("");
    }
  }, [needsJobIndustry, jobIndustry]);

  const syncBirthToAccount = useCallback(() => {
    setAccountFormData((prev) => ({
      ...prev,
      birth_year: birthYear === "" ? null : birthYear,
      birth_month: birthMonth === "" ? null : birthMonth,
    }));
  }, [birthYear, birthMonth, setAccountFormData]);

  const syncJobToAccount = useCallback(() => {
    setAccountFormData((prev) => ({
      ...prev,
      job_category: jobCategory,
      job_industry: needsJobIndustry ? jobIndustry : null,
      job_continuous_period: jobContinuousPeriod,
      annual_income: annualIncome.trim(),
    }));
  }, [annualIncome, jobCategory, jobContinuousPeriod, jobIndustry, needsJobIndustry, setAccountFormData]);

  const syncAssetToAccount = useCallback(() => {
    setAccountFormData((prev) => ({
      ...prev,
      financial_asset: financialAssetRange,
      // 経験なしの場合は保有数・ローンを未入力扱い（0/空）として先に進める
      holding_property_count: showPropertyOwnershipFields ? holdingPropertyCount : "0",
      real_estate_loan_amount: showPropertyOwnershipFields && needsLoanAmount ? realEstateLoanAmount.trim() : "",
    }));
  }, [
    financialAssetRange,
    holdingPropertyCount,
    needsLoanAmount,
    realEstateLoanAmount,
    showPropertyOwnershipFields,
    setAccountFormData,
  ]);

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const annualIncomeValidation = annualIncomeSchema.safeParse(annualIncome);
  const annualIncomeError = !annualIncomeValidation.success && touched.income
    ? annualIncomeValidation.error.issues[0]?.message
    : undefined;
  const touch = (name: string) => setTouched((prev) => ({ ...prev, [name]: true }));
  const fieldError = (name: string, value: string) => (touched[name] && !value ? "選択してください" : undefined);
  const attempt = (valid: boolean, names: string[], next: () => void) => {
    if (valid) {
      next();
      return;
    }
    setTouched((prev) => ({ ...prev, ...Object.fromEntries(names.map((name) => [name, true])) }));
    window.setTimeout(() => {
      const field = document.querySelector<HTMLElement>("[data-registration-flow] [aria-invalid=\"true\"]");
      field?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      field?.focus();
    }, 0);
  };
  if (currentStep === STEP_LOADING) {
    return (
      <RegistrationTemplate centered>
        {videoEnded ? (
          <Typography className={classes.loadingDoneText}>診断が完了しました！</Typography>
        ) : (
          <>
            <Typography className={classes.loadingHeading}>投資力を診断中です…</Typography>
            <AnimationVideo
              className={classes.loadingVideo}
              src={LoadingVideo}
              poster={LoadingPoster}
              onEnded={handleVideoEnd}
              onError={handleVideoEnd}
            />
            <Box mt={3}>
              <LinearProgress aria-label="投資力を診断中" />
            </Box>
          </>
        )}
      </RegistrationTemplate>
    );
  }

  if (currentStep === STEP_RESULT) {
    return (
      <RegistrationTemplate phaseTags={<RegistrationPhases completed />}>
        <Box className={classes.resultReveal} data-diagnosis-result-reveal="heading">
          <Typography className={classes.resultTitle}>診断が完了しました</Typography>
          <Typography className={classes.resultDescription}>答えていただいた内容から、あなたの投資力を出しました。</Typography>
        </Box>
        <Box
          className={`${classes.card} ${classes.resultCard} ${classes.resultReveal} ${classes.resultCardReveal}`}
          data-diagnosis-result-reveal="card"
        >
          <Typography className={classes.potentialLabel}>推定運用可能資産額</Typography>
          <Box className={classes.blurredArea}>
            <Box aria-hidden="true" className={classes.blurredContent} data-diagnosis-result-blurred>
              <Box className={classes.assetSection}>
                <Box className={classes.potentialValueWrap}>
                  <Typography className={classes.potentialValueNumber + " " + classes.blurText}>3456</Typography>
                  <Typography className={classes.potentialValueUnit + " " + classes.blurText}>万円</Typography>
                  <Typography className={classes.potentialValueSuffix + " " + classes.blurText}>前後</Typography>
                </Box>
              </Box>
              <Divider className={classes.cardDivider} />
              <Box className={classes.propertySection}>
                <img src={PropertyImage} alt="物件例" className={classes.propertyImage} />
                <Box className={classes.propertyText}>
                  <Typography className={classes.propertyHeading + " " + classes.blurText}>
                    この価格帯で検討できる物件イメージ
                  </Typography>
                  <Typography className={classes.propertyTitle + " " + classes.blurText}>
                    東京23区 駅徒歩10分以内
                  </Typography>
                  <Typography className={classes.propertySpeedLabel + " " + classes.blurText}>
                    資産形成スピード
                  </Typography>
                  <Typography className={classes.propertySpeedValue + " " + classes.blurText}>
                    12345
                    <span className={classes.propertySpeedValueMeta + " " + classes.blurText}>円/月</span>
                  </Typography>
                  <Typography className={classes.wealthNote + " " + classes.blurText}>
                    ※毎月の手取りではありません
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box className={classes.registerBadge}>
              <img src="/icons/ds-v2-eye-off.svg" alt="" aria-hidden="true" className={classes.registerBadgeIcon} />
              アカウントを作成すると表示されます
            </Box>
          </Box>
        </Box>

        <Box
          mb={3}
          className={`${classes.resultReveal} ${classes.answersReveal}`}
          data-diagnosis-result-reveal="answers"
        >
          <Typography style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>あなたの回答</Typography>
          <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
            {[
              birthYear && birthMonth
                ? `${new Date().getFullYear() - birthYear - (new Date().getMonth() + 1 < birthMonth ? 1 : 0)}歳`
                : "",
              jobCategory,
              annualIncome ? `年収 ${annualIncome}万円` : "",
              financialAssetRange ? `金融資産 ${financialAssetRange}` : "",
              holdingPropertyCountNumber > 0 ? `投資用不動産 ${holdingPropertyCountNumber}件` : "投資用不動産 なし",
            ]
              .filter(Boolean)
              .map((answer) => (
                <CustomChip className={classes.answerChip} key={answer} label={String(answer)} outlined />
              ))}
          </Box>
        </Box>
        <Box
          className={`${classes.resultCard} ${classes.resultReveal} ${classes.featuresReveal}`}
          data-diagnosis-result-reveal="features"
        >
          <Typography className={classes.featuresLead}>アカウントを作ると見られるもの</Typography>
          <Box className={classes.featuresBox}>
            <Box className={classes.featureItem}>
              <CheckIcon style={{ fontSize: 16, color: theme.palette.semantic["status/success"], flexShrink: 0 }} />
              <Typography component="span" className={classes.featureText}>
                <span className={classes.featureHighlight}>運用可能資産額</span>の正確な金額
              </Typography>
            </Box>
            <Box className={classes.featureItem}>
              <CheckIcon style={{ fontSize: 16, color: theme.palette.semantic["status/success"], flexShrink: 0 }} />
              <Typography component="span" className={classes.featureText}>
                あなたが買える<span className={classes.featureHighlight}>物件の例</span>
              </Typography>
            </Box>
            <Box className={classes.featureItem}>
              <CheckIcon style={{ fontSize: 16, color: theme.palette.semantic["status/success"], flexShrink: 0 }} />
              <Typography component="span" className={classes.featureText}>
                月あたりの<span className={classes.featureHighlight}>資産形成効果</span>
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          className={`${classes.resultReveal} ${classes.actionsReveal}`}
          data-diagnosis-result-reveal="actions"
        >
          <Box className={classes.buttonWrap}>
            <CustomButton
              type="button"
              customVariant="filled"
              onClick={() => {
                syncAssetToAccount();
                history.push("/registrations");
              }}
            >
              無料で診断結果を保存する
            </CustomButton>
          </Box>
          <Typography style={{ textAlign: "center", fontSize: 12, color: theme.palette.text.secondary, marginTop: 12 }}>
            登録は1分。結果は保存され、あとから見返せます。
          </Typography>
        </Box>
      </RegistrationTemplate>
    );
  }

  const questionCount = needsLoanAmount ? 5 : 4;
  const titles = [
    "生まれた年月を\n教えてください",
    "お仕事について\n教えてください",
    "年収を教えてください",
    "資産について\n教えてください",
    "投資用不動産ローンの\n残債を教えてください",
  ];
  const descriptions = [
    "年齢に合った投資の進め方をご提案します",
    "融資の受けやすさの判断に使います",
    "税引き前の金額（額面）を入力してください",
    "自己資金から購入できる価格帯を算出します",
    "住宅ローンは含みません。無ければ0のままで大丈夫です",
  ];
  return (
    <RegistrationTemplate phaseTags={<RegistrationPhases />}>
      <Box mb={3}>
        <LinearProgress style={{ height: 3 }} variant="determinate" value={((currentStep + 1) / questionCount) * 100} />
        <Typography className={classes.progressCaption}>
          {currentStep + 1} / {questionCount}
        </Typography>
      </Box>
      <Typography component="h1" className={classes.questionTitle}>
        {titles[currentStep]}
      </Typography>
      <Typography className={classes.questionDescription}>{descriptions[currentStep]}</Typography>
      <Box mt={3}>
        {currentStep === STEP_AGE && (
          <AgeStep
            birthYear={birthYear}
            birthMonth={birthMonth}
            onChangeBirthYear={setBirthYear}
            onChangeBirthMonth={setBirthMonth}
            yearOptions={YEAR_OPTIONS_ASC}
            monthOptions={MONTH_OPTIONS}
            isValid={isAgeStepValid}
            onNext={() => {
              syncBirthToAccount();
              setCurrentStep(STEP_JOB);
            }}
          />
        )}
        {currentStep === STEP_JOB && (
          <JobStep
            jobCategory={jobCategory}
            jobIndustry={jobIndustry}
            jobContinuousPeriod={jobContinuousPeriod}
            onChangeJobCategory={setJobCategory}
            onChangeJobIndustry={setJobIndustry}
            onChangeJobContinuousPeriod={setJobContinuousPeriod}
            occupationOptions={OCCUPATION_OPTIONS}
            jobIndustryOptions={JOB_CATEGORY_OPTIONS}
            periodRangeOptions={PERIOD_RANGE_OPTIONS}
            needsJobIndustry={needsJobIndustry}
            onBlur={touch}
            errors={{
              job: fieldError("job", jobCategory),
              industry: needsJobIndustry ? fieldError("industry", jobIndustry) : undefined,
              period: fieldError("period", jobContinuousPeriod),
            }}
            onNext={() => attempt(isJobStepValid, ["job", "industry", "period"], () => setCurrentStep(STEP_INCOME))}
            onBack={() => setCurrentStep(STEP_AGE)}
          />
        )}
        {currentStep === STEP_INCOME && (
          <>
            <NumberEntry
              label="年収"
              value={annualIncome}
              onChange={setAnnualIncome}
              onBlur={() => touch("income")}
              error={annualIncomeError}
              unit="万円"
            />
            <RegistrationActions
              disabled={!annualIncome.trim()}
              onNext={() => attempt(annualIncomeValidation.success, ["income"], () => {
                syncJobToAccount();
                setCurrentStep(STEP_ASSET);
              })}
              onBack={() => setCurrentStep(STEP_JOB)}
            />
          </>
        )}
        {currentStep === STEP_ASSET && (
          <AssetStep
            financialAssetRange={financialAssetRange}
            holdingPropertyCount={holdingPropertyCount}
            onChangeFinancialAssetRange={setFinancialAssetRange}
            onChangeHoldingPropertyCount={setHoldingPropertyCount}
            loanRangeOptions={LOAN_RANGE_OPTIONS}
            showPropertyOwnershipFields={showPropertyOwnershipFields}
            last={!needsLoanAmount}
            onBlur={touch}
            errors={{
              asset: fieldError("asset", financialAssetRange),
              count: showPropertyOwnershipFields ? fieldError("count", holdingPropertyCount) : undefined,
            }}
            onNext={() =>
              attempt(
                financialAssetRange !== "" && (!showPropertyOwnershipFields || holdingPropertyCount !== ""),
                ["asset", "count"],
                () => {
                  if (needsLoanAmount) setCurrentStep(STEP_LOAN);
                  else {
                    syncAssetToAccount();
                    setCurrentStep(STEP_LOADING);
                  }
                }
              )
            }
            onBack={() => setCurrentStep(STEP_INCOME)}
          />
        )}
        {currentStep === STEP_LOAN && (
          <>
            <NumberEntry
              label="投資用不動産ローンの残債"
              value={realEstateLoanAmount}
              onChange={setRealEstateLoanAmount}
              unit="万円"
            />
            <RegistrationActions
              disabled={!realEstateLoanAmount.trim()}
              label="診断する"
              onNext={() => {
                syncAssetToAccount();
                setCurrentStep(STEP_LOADING);
              }}
              onBack={() => setCurrentStep(STEP_ASSET)}
            />
          </>
        )}
      </Box>
    </RegistrationTemplate>
  );
};
